/**
 * Storage Manager - Practical Data Persistence
 * 
 * Provides offline-first data storage using IndexedDB with localStorage fallback
 * Enables users to track their spiritual practice, save work, and export data
 */

// ============================================================================
// INDEXEDDB SETUP
// ============================================================================

const DB_NAME = 'Fr00tSpiritualDB';
const DB_VERSION = 1;

export interface MeditationSession {
  id: string;
  type: string;
  duration: number; // seconds
  completedAt: string; // ISO timestamp
  intention?: string;
  notes?: string;
  breathCount?: number;
  frequency?: number;
}

export interface JournalEntry {
  id: string;
  title: string;
  content: string;
  mood?: 'joyful' | 'peaceful' | 'reflective' | 'challenging' | 'grateful';
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AudioPreset {
  id: string;
  name: string;
  type: 'tone' | 'chord' | 'scale';
  frequency?: number;
  chord?: string;
  scale?: string;
  duration?: number;
  waveform?: 'sine' | 'square' | 'sawtooth' | 'triangle';
  volume?: number;
  createdAt: string;
}

export interface HabitTracker {
  id: string;
  name: string;
  description: string;
  frequency: 'daily' | 'weekly' | 'custom';
  completions: string[]; // ISO dates when completed
  streak: number;
  createdAt: string;
}

export interface NumerologyReading {
  id: string;
  inputText: string;
  results: any;
  savedAt: string;
  notes?: string;
}

export interface CreativeProject {
  id: string;
  title: string;
  type: 'audio' | 'visual' | 'writing' | 'mixed';
  description: string;
  content: any;
  status: 'draft' | 'in-progress' | 'completed';
  createdAt: string;
  updatedAt: string;
}

// Initialize IndexedDB
class StorageManager {
  private db: IDBDatabase | null = null;
  private isInitialized = false;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB');
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        console.log('✅ Storage Manager initialized');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores
        if (!db.objectStoreNames.contains('meditations')) {
          const meditationStore = db.createObjectStore('meditations', { keyPath: 'id' });
          meditationStore.createIndex('completedAt', 'completedAt', { unique: false });
          meditationStore.createIndex('type', 'type', { unique: false });
        }

        if (!db.objectStoreNames.contains('journal')) {
          const journalStore = db.createObjectStore('journal', { keyPath: 'id' });
          journalStore.createIndex('createdAt', 'createdAt', { unique: false });
          journalStore.createIndex('mood', 'mood', { unique: false });
        }

        if (!db.objectStoreNames.contains('audioPresets')) {
          db.createObjectStore('audioPresets', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('habits')) {
          const habitStore = db.createObjectStore('habits', { keyPath: 'id' });
          habitStore.createIndex('name', 'name', { unique: false });
        }

        if (!db.objectStoreNames.contains('numerologyReadings')) {
          const numerologyStore = db.createObjectStore('numerologyReadings', { keyPath: 'id' });
          numerologyStore.createIndex('savedAt', 'savedAt', { unique: false });
        }

        if (!db.objectStoreNames.contains('projects')) {
          const projectStore = db.createObjectStore('projects', { keyPath: 'id' });
          projectStore.createIndex('updatedAt', 'updatedAt', { unique: false });
          projectStore.createIndex('status', 'status', { unique: false });
        }

        console.log('✅ Database schema created');
      };
    });
  }

  // ============================================================================
  // MEDITATION SESSIONS
  // ============================================================================

  async saveMeditationSession(session: Omit<MeditationSession, 'id'>): Promise<string> {
    await this.ensureInitialized();
    const id = `med_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fullSession: MeditationSession = { id, ...session };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['meditations'], 'readwrite');
      const store = transaction.objectStore('meditations');
      const request = store.add(fullSession);

      request.onsuccess = () => resolve(id);
      request.onerror = () => reject(request.error);
    });
  }

  async getMeditationSessions(limit = 50): Promise<MeditationSession[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['meditations'], 'readonly');
      const store = transaction.objectStore('meditations');
      const index = store.index('completedAt');
      const request = index.openCursor(null, 'prev'); // Descending order

      const sessions: MeditationSession[] = [];

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor && sessions.length < limit) {
          sessions.push(cursor.value);
          cursor.continue();
        } else {
          resolve(sessions);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  async getMeditationStats(): Promise<{
    totalSessions: number;
    totalMinutes: number;
    streak: number;
    favoriteType: string;
    weeklyAverage: number;
  }> {
    const sessions = await this.getMeditationSessions(1000);
    const totalSessions = sessions.length;
    const totalMinutes = Math.round(sessions.reduce((sum, s) => sum + s.duration, 0) / 60);

    // Calculate streak (consecutive days with meditation)
    const dates = sessions.map(s => new Date(s.completedAt).toDateString());
    const uniqueDates = Array.from(new Set(dates)).sort().reverse();
    let streak = 0;
    let currentDate = new Date();

    for (const dateStr of uniqueDates) {
      const sessionDate = new Date(dateStr);
      const diffDays = Math.floor((currentDate.getTime() - sessionDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffDays === streak) {
        streak++;
      } else {
        break;
      }
      currentDate = sessionDate;
    }

    // Most common type
    const typeCounts = sessions.reduce((acc, s) => {
      acc[s.type] = (acc[s.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const favoriteType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'mindfulness';

    // Weekly average
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const weekSessions = sessions.filter(s => new Date(s.completedAt) >= oneWeekAgo);
    const weeklyAverage = weekSessions.length;

    return { totalSessions, totalMinutes, streak, favoriteType, weeklyAverage };
  }

  // ============================================================================
  // JOURNAL ENTRIES
  // ============================================================================

  async saveJournalEntry(entry: Omit<JournalEntry, 'id'>): Promise<string> {
    await this.ensureInitialized();
    const id = `journal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fullEntry: JournalEntry = { id, ...entry };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['journal'], 'readwrite');
      const store = transaction.objectStore('journal');
      const request = store.add(fullEntry);

      request.onsuccess = () => resolve(id);
      request.onerror = () => reject(request.error);
    });
  }

  async updateJournalEntry(id: string, updates: Partial<JournalEntry>): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['journal'], 'readwrite');
      const store = transaction.objectStore('journal');
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const entry = getRequest.result;
        if (entry) {
          const updated = { ...entry, ...updates, updatedAt: new Date().toISOString() };
          const putRequest = store.put(updated);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error('Entry not found'));
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async getJournalEntries(limit = 50): Promise<JournalEntry[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['journal'], 'readonly');
      const store = transaction.objectStore('journal');
      const index = store.index('createdAt');
      const request = index.openCursor(null, 'prev');

      const entries: JournalEntry[] = [];

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor && entries.length < limit) {
          entries.push(cursor.value);
          cursor.continue();
        } else {
          resolve(entries);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  async deleteJournalEntry(id: string): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['journal'], 'readwrite');
      const store = transaction.objectStore('journal');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================================================
  // AUDIO PRESETS
  // ============================================================================

  async saveAudioPreset(preset: Omit<AudioPreset, 'id' | 'createdAt'>): Promise<string> {
    await this.ensureInitialized();
    const id = `preset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fullPreset: AudioPreset = { 
      id, 
      createdAt: new Date().toISOString(),
      ...preset 
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['audioPresets'], 'readwrite');
      const store = transaction.objectStore('audioPresets');
      const request = store.add(fullPreset);

      request.onsuccess = () => resolve(id);
      request.onerror = () => reject(request.error);
    });
  }

  async getAudioPresets(): Promise<AudioPreset[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['audioPresets'], 'readonly');
      const store = transaction.objectStore('audioPresets');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteAudioPreset(id: string): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['audioPresets'], 'readwrite');
      const store = transaction.objectStore('audioPresets');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================================================
  // HABIT TRACKING
  // ============================================================================

  async saveHabit(habit: Omit<HabitTracker, 'id' | 'createdAt' | 'streak'>): Promise<string> {
    await this.ensureInitialized();
    const id = `habit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fullHabit: HabitTracker = {
      id,
      createdAt: new Date().toISOString(),
      streak: 0,
      ...habit
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['habits'], 'readwrite');
      const store = transaction.objectStore('habits');
      const request = store.add(fullHabit);

      request.onsuccess = () => resolve(id);
      request.onerror = () => reject(request.error);
    });
  }

  async updateHabit(id: string, updates: Partial<HabitTracker>): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['habits'], 'readwrite');
      const store = transaction.objectStore('habits');
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const habit = getRequest.result;
        if (habit) {
          const updated = { ...habit, ...updates };
          const putRequest = store.put(updated);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error('Habit not found'));
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async completeHabit(id: string): Promise<void> {
    await this.ensureInitialized();

    const transaction = this.db!.transaction(['habits'], 'readwrite');
    const store = transaction.objectStore('habits');
    const getRequest = store.get(id);

    return new Promise((resolve, reject) => {
      getRequest.onsuccess = () => {
        const habit = getRequest.result as HabitTracker;
        if (habit) {
          const today = new Date().toISOString().split('T')[0];
          
          // Don't add duplicate completion for today
          if (!habit.completions.includes(today)) {
            habit.completions.push(today);
            
            // Calculate streak
            const sortedDates = [...habit.completions].sort().reverse();
            let streak = 0;
            let currentDate = new Date();

            for (const dateStr of sortedDates) {
              const completionDate = new Date(dateStr);
              const diffDays = Math.floor((currentDate.getTime() - completionDate.getTime()) / (1000 * 60 * 60 * 24));
              
              if (diffDays === streak) {
                streak++;
                currentDate = completionDate;
              } else {
                break;
              }
            }

            habit.streak = streak;
          }

          const putRequest = store.put(habit);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error('Habit not found'));
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async getHabits(): Promise<HabitTracker[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['habits'], 'readonly');
      const store = transaction.objectStore('habits');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteHabit(id: string): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['habits'], 'readwrite');
      const store = transaction.objectStore('habits');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================================================
  // NUMEROLOGY READINGS
  // ============================================================================

  async saveNumerologyReading(reading: Omit<NumerologyReading, 'id' | 'savedAt'>): Promise<string> {
    await this.ensureInitialized();
    const id = `num_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fullReading: NumerologyReading = {
      id,
      savedAt: new Date().toISOString(),
      ...reading
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['numerologyReadings'], 'readwrite');
      const store = transaction.objectStore('numerologyReadings');
      const request = store.add(fullReading);

      request.onsuccess = () => resolve(id);
      request.onerror = () => reject(request.error);
    });
  }

  async getNumerologyReadings(limit = 50): Promise<NumerologyReading[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['numerologyReadings'], 'readonly');
      const store = transaction.objectStore('numerologyReadings');
      const index = store.index('savedAt');
      const request = index.openCursor(null, 'prev');

      const readings: NumerologyReading[] = [];

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor && readings.length < limit) {
          readings.push(cursor.value);
          cursor.continue();
        } else {
          resolve(readings);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  async deleteNumerologyReading(id: string): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['numerologyReadings'], 'readwrite');
      const store = transaction.objectStore('numerologyReadings');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================================================
  // CREATIVE PROJECTS
  // ============================================================================

  async saveProject(project: Omit<CreativeProject, 'id'>): Promise<string> {
    await this.ensureInitialized();
    const id = `proj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fullProject: CreativeProject = { id, ...project };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['projects'], 'readwrite');
      const store = transaction.objectStore('projects');
      const request = store.add(fullProject);

      request.onsuccess = () => resolve(id);
      request.onerror = () => reject(request.error);
    });
  }

  async updateProject(id: string, updates: Partial<CreativeProject>): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['projects'], 'readwrite');
      const store = transaction.objectStore('projects');
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const project = getRequest.result;
        if (project) {
          const updated = { ...project, ...updates, updatedAt: new Date().toISOString() };
          const putRequest = store.put(updated);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error('Project not found'));
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async getProjects(): Promise<CreativeProject[]> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['projects'], 'readonly');
      const store = transaction.objectStore('projects');
      const index = store.index('updatedAt');
      const request = index.openCursor(null, 'prev');

      const projects: CreativeProject[] = [];

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          projects.push(cursor.value);
          cursor.continue();
        } else {
          resolve(projects);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  async deleteProject(id: string): Promise<void> {
    await this.ensureInitialized();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['projects'], 'readwrite');
      const store = transaction.objectStore('projects');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================================================
  // DATA EXPORT
  // ============================================================================

  async exportAllData(): Promise<any> {
    await this.ensureInitialized();

    const [
      meditations,
      journal,
      presets,
      habits,
      readings,
      projects
    ] = await Promise.all([
      this.getMeditationSessions(10000),
      this.getJournalEntries(10000),
      this.getAudioPresets(),
      this.getHabits(),
      this.getNumerologyReadings(10000),
      this.getProjects()
    ]);

    return {
      exportDate: new Date().toISOString(),
      version: '1.0.0',
      data: {
        meditations,
        journal,
        audioPresets: presets,
        habits,
        numerologyReadings: readings,
        projects
      }
    };
  }

  async downloadExport(): Promise<void> {
    const data = await this.exportAllData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fr00t-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ============================================================================
  // UTILITY
  // ============================================================================

  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }
  }
}

// Singleton instance
export const storage = new StorageManager();

// Auto-initialize on import
storage.initialize().catch(err => {
  console.warn('Storage initialization failed, using fallback:', err);
});

export default storage;

